import * as api from '@apis/statistics';
import DailyPurchaseSourceChart from '@components/charts/DailyPurchaseSourceChart';
import ProductClickLocationChart from '@components/dashboard/ProductClickLocationChart';
import ProductClickLocationRatioChart from '@components/dashboard/ProductClickLocationRatioChart';
import DiscoverFeedStatsCards from '@components/dashboard/DiscoverFeedStatsCards';
import ProductInteractionChart from '@components/dashboard/ProductInteractionChart';
import SessionDurationChart from '@components/dashboard/SessionDurationChart';
import { useStatistics } from '@hooks/statistics';
import { PURCHASE_SOURCE_NAME_MAP } from '@models/statistics';
import {
  BACKGROUND_COLORS,
  CHART_CATEGORY_COLORS,
  CHART_CATEGORY_LABELS,
  CHART_MISC_COLORS,
  CHART_MISC_LABELS,
} from '@pages/Dashboard';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Table } from 'antd';
import { Chart } from 'chart.js';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import ClipLoader from 'react-spinners/ClipLoader';
import '../../pages/Dashboard.scss';

export const UserBehaviorTab: React.FC = () => {
  const {
    statisticsStore,
    isLoadingSearchText,
    isLoadingDateSearchRank,
    isLoadingNoResultSearchQuery,
    isLoadingDailyProductView,
    isLoadingDailyPaymentError,
    isLoadingDailyPaymentReady,
    isLoadingPaymentCounts,
    isLoadingClicksRank,
    isLoadingSessionDuration,
    isLoadingProductInteraction,
    isLoadingProductClickLocationData,
    isLoadingDiscoverFeedStats,
    discoverFeedStats,
    isLoadingPartnerProfileViewCounts,
    isLoadingPartnerProfileClickCounts,
    isLoadingDailyPurchaseSourceCounts,
    isLoadingPurchaseSourceCounts,
    isLoadingBuyerRegionCounts,
    isLoadingProductViewsByCategoryWeekly,
    isLoadingDailyClassCounts,
    isLoadingDailyFavoriteProductCounts,
    isLoadingDailyFavoriteUserContentsCounts,
    isLoadingDailyFavoritePartnerCounts,
  } = useStatistics('behavior');

  // 다중지능 검사 데이터 쿼리
  const { data: dailyAssessmentCounts, isLoading: isLoadingDailyAssessmentCounts } = useQuery({
    queryKey: ['daily-assessment-counts', statisticsStore.startDate, statisticsStore.endDate],
    queryFn: () =>
      api.getDailyAssessmentCounts(
        dayjs(statisticsStore.startDate).format('YYYY-MM-DD'),
        dayjs(statisticsStore.endDate).format('YYYY-MM-DD'),
      ),
    enabled: !!statisticsStore.startDate && !!statisticsStore.endDate,
  });

  const chatNewDiff = (statisticsStore.chatCounts?.new || 0) - (statisticsStore.chatCounts?.samePastPeriod?.new || 0);
  const chatClassDiff =
    (statisticsStore.chatCounts?.class || 0) - (statisticsStore.chatCounts?.samePastPeriod?.class || 0);
  const chatUserDiff =
    (statisticsStore.chatCounts?.user || 0) - (statisticsStore.chatCounts?.samePastPeriod?.user || 0);

  const searchTextData = {
    labels: (statisticsStore.searchTextRank || []).map((item) => item._id),
    datasets: [
      {
        data: (statisticsStore.searchTextRank || []).map((item) => item.count),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const noResultSearchTextData = {
    labels: (statisticsStore.noResultSearchTextRank || []).map((item) => item._id),
    datasets: [
      {
        data: (statisticsStore.noResultSearchTextRank || []).map((item) => item.count),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const dateSearchRankData = {
    labels: (statisticsStore.dateSearchRank?.rank || []).map((item) => item._id),
    datasets: [
      {
        data: (statisticsStore.dateSearchRank?.rank || []).map((item) => item.count),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const buyerRegionCountsData = {
    labels: (statisticsStore.buyerRegionCounts || []).slice(0, 30).map((item) => item._id),
    datasets: [
      {
        label: '구매자 수',
        data: (statisticsStore.buyerRegionCounts || []).slice(0, 30).map((item) => item.count),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  // 상품 카테고리별 조회수 데이터 생성
  const generateCategoryColors = (index: number) => {
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#C9CBCF',
      '#FF5722',
      '#795548',
      '#607D8B',
    ];
    return colors[index % colors.length];
  };

  const productViewsByCategoryWeeklyData = useMemo(() => {
    const data = statisticsStore.productViewsByCategoryWeekly || [];
    if (data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = data.map((item) => item.week);
    const categories = Object.keys(data[0] || {}).filter((key) => key !== 'week' && key !== 'isPrediction');

    // Separate actual and prediction data
    const actualData = data.filter((item) => !item.isPrediction);
    const predictionData = data.filter((item) => item.isPrediction);

    const datasets: any[] = [];

    categories.forEach((category, index) => {
      const color = generateCategoryColors(index);

      // Actual data dataset
      const actualValues = data.map((item) => (!item.isPrediction ? item[category] || 0 : null));
      datasets.push({
        label: category,
        data: actualValues,
        borderColor: color,
        backgroundColor: color + '20',
        fill: false,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
      });

      // Prediction data dataset (if exists)
      if (predictionData.length > 0) {
        // Create connection between last actual and first prediction
        const lastActualIndex = actualData.length - 1;
        const connectionData = data.map((item, index) => {
          if (index === lastActualIndex) {
            return item[category] || 0;
          } else if (item.isPrediction) {
            return item[category] || 0;
          }
          return null;
        });

        datasets.push({
          label: `${category} (예측)`,
          data: connectionData,
          borderColor: color,
          backgroundColor: color + '10',
          borderDash: [5, 5], // Dashed line for predictions
          fill: false,
          tension: 0.1,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointStyle: 'triangle',
        });
      }
    });

    return { labels, datasets };
  }, [statisticsStore.productViewsByCategoryWeekly]);

  const productViewsByCategoryWeeklyRatiosData = useMemo(() => {
    const data = statisticsStore.productViewsByCategoryWeeklyRatios || [];
    if (data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = data.map((item) => item.week);
    const categories = Object.keys(data[0] || {}).filter((key) => key !== 'week' && key !== 'isPrediction');

    // Separate actual and prediction data
    const actualData = data.filter((item) => !item.isPrediction);
    const predictionData = data.filter((item) => item.isPrediction);

    const datasets: any[] = [];

    categories.forEach((category, index) => {
      const color = generateCategoryColors(index);

      // Actual data dataset
      const actualValues = data.map((item) => (!item.isPrediction ? item[category] || 0 : null));
      datasets.push({
        label: category,
        data: actualValues,
        borderColor: color,
        backgroundColor: color + '20',
        fill: false,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
      });

      // Prediction data dataset (if exists)
      if (predictionData.length > 0) {
        // Create connection between last actual and first prediction
        const lastActualIndex = actualData.length - 1;
        const connectionData = data.map((item, index) => {
          if (index === lastActualIndex) {
            return item[category] || 0;
          } else if (item.isPrediction) {
            return item[category] || 0;
          }
          return null;
        });

        datasets.push({
          label: `${category} (예측)`,
          data: connectionData,
          borderColor: color,
          backgroundColor: color + '10',
          borderDash: [5, 5], // Dashed line for predictions
          fill: false,
          tension: 0.1,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointStyle: 'triangle',
        });
      }
    });

    return { labels, datasets };
  }, [statisticsStore.productViewsByCategoryWeeklyRatios]);

  // 일별 수업 인원 차트 데이터 생성
  const dailyClassCountsData = useMemo(() => {
    const data = statisticsStore.dailyClassCounts || [];
    if (data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = data.map((item) => {
      const date = new Date(item.date);
      const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      return `${item.date} (${dayOfWeek})`;
    });

    // Separate actual and prediction data
    const actualData = data.filter((item) => !item.isPrediction);
    const predictionData = data.filter((item) => item.isPrediction);

    const datasets: any[] = [];

    // Actual data dataset
    const actualValues = data.map((item) => (!item.isPrediction ? item.count : null));
    datasets.push({
      label: '수업 인원',
      data: actualValues,
      borderColor: '#36A2EB',
      backgroundColor: '#36A2EB20',
      fill: true,
      tension: 0.1,
      pointRadius: 4,
      pointHoverRadius: 6,
    });

    // Prediction data dataset (if exists)
    if (predictionData.length > 0) {
      // Create connection between last actual and first prediction
      const lastActualIndex = actualData.length - 1;
      const connectionData = data.map((item, index) => {
        if (index === lastActualIndex) {
          return item.count;
        } else if (item.isPrediction) {
          return item.count;
        }
        return null;
      });

      datasets.push({
        label: '수업 인원 (예측)',
        data: connectionData,
        borderColor: '#FF6384',
        backgroundColor: '#FF638410',
        borderDash: [5, 5], // Dashed line for predictions
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointStyle: 'triangle',
      });
    }

    return { labels, datasets };
  }, [statisticsStore.dailyClassCounts]);

  const dailyProductViewLabels: string[] = [];
  const dailyProductViews: any = {};
  const dailyProducts: any = {};
  (statisticsStore.dailyProductView || []).forEach((item) => {
    if (!dailyProductViewLabels.includes(item._id)) {
      dailyProductViewLabels.push(item._id);
    }
    dailyProductViews[`${item._id}_${item.productId}`] = item.view;
    dailyProducts[item.productId] = { name: `${item.productName?.slice(0, 20)}...`, data: [] };
  });

  dailyProductViewLabels.forEach((day) => {
    Object.keys(dailyProducts).forEach((key) => {
      dailyProducts[key].data.push(dailyProductViews[`${day}_${key}`] || 0);
    });
  });

  const dailyProductViewData = {
    labels: dailyProductViewLabels,
    datasets: Object.keys(dailyProducts).map((key) => {
      return {
        label: dailyProducts[key].name,
        data: dailyProducts[key].data,
      };
    }),
  };

  const dailyPaymentErrorCountData = {
    labels: (statisticsStore.dailyPaymentError || []).map(
      (item) => `${item._id.year}-${item._id.month}-${item._id.day}`,
    ),
    datasets: [
      {
        label: CHART_MISC_LABELS.user,
        borderColor: CHART_MISC_COLORS.user,
        backgroundColor: CHART_MISC_COLORS.user,
        data: (statisticsStore.dailyPaymentError || []).map((item) => item.buyerCount),
      },
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPaymentError || []).map((item) => item.class),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPaymentError || []).map((item) => item.commerce),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPaymentError || []).map((item) => item.commerce + item.class),
      },
    ],
  };

  const dailyPaymentReadyCountData = {
    labels: (statisticsStore.dailyPaymentReady || []).map(
      (item) => `${item._id.year}-${item._id.month}-${item._id.day}`,
    ),
    datasets: [
      {
        label: CHART_MISC_LABELS.user,
        borderColor: CHART_MISC_COLORS.user,
        backgroundColor: CHART_MISC_COLORS.user,
        data: (statisticsStore.dailyPaymentReady || []).map((item) => item.buyerCount),
      },
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPaymentReady || []).map((item) => item.class),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPaymentReady || []).map((item) => item.commerce),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPaymentReady || []).map((item) => item.commerce + item.class),
      },
    ],
  };

  // 다중지능 검사 차트 데이터 생성
  const dailyAssessmentCountsData = useMemo(() => {
    const data = dailyAssessmentCounts?.data?.dailyData || [];
    if (data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = data.map((item) => {
      const date = new Date(item._id);
      const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      return `${item._id} (${dayOfWeek})`;
    });

    const datasets = [
      {
        label: '일별 검사수',
        data: data.map((item) => item.assessmentCount),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        fill: true,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      },
      {
        label: '일별 검사 유저수',
        data: data.map((item) => item.userCount),
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        fill: false,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      },
    ];

    return { labels, datasets };
  }, [dailyAssessmentCounts]);

  return (
    <div>
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '12px',
          }}>
          <div className="count-card">
            <div className="count-title">신규 채팅상담 신청</div>
            {isLoadingPaymentCounts && <ClipLoader />}
            {!isLoadingPaymentCounts && (
              <div className="count-number">
                {`${statisticsStore.chatCounts?.new}`}
                <span
                  className="count-suffix"
                  style={{ color: chatNewDiff > 0 ? 'green' : 'red' }}>{`${chatNewDiff}`}</span>
              </div>
            )}
          </div>
          <div className="count-card">
            <div className="count-title">채팅상담 진행 클래스</div>
            {isLoadingPaymentCounts && <ClipLoader />}
            {!isLoadingPaymentCounts && (
              <div className="count-number">
                {`${statisticsStore.chatCounts?.class}`}
                <span
                  className="count-suffix"
                  style={{ color: chatClassDiff > 0 ? 'green' : 'red' }}>{`${chatClassDiff}`}</span>
              </div>
            )}
          </div>
          <div className="count-card">
            <div className="count-title">채팅상담 진행 부모</div>
            {isLoadingPaymentCounts && <ClipLoader />}
            {!isLoadingPaymentCounts && (
              <div className="count-number">
                {`${statisticsStore.chatCounts?.user}`}
                <span
                  className="count-suffix"
                  style={{ color: chatUserDiff > 0 ? 'green' : 'red' }}>{`${chatUserDiff}`}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Card className="chart" title="세션 체류시간" bordered={false}>
          {isLoadingSessionDuration && <ClipLoader />}
          {!isLoadingSessionDuration && statisticsStore.sessionDuration && (
            <SessionDurationChart data={statisticsStore.sessionDuration} />
          )}
        </Card>
        <Card className="chart" title="상품 상호작용 비율" bordered={false}>
          {isLoadingProductInteraction && <ClipLoader />}
          {!isLoadingProductInteraction && statisticsStore.productInteraction && (
            <ProductInteractionChart data={statisticsStore.productInteraction} />
          )}
        </Card>
        <Card className="chart" title="일별 상품 뷰 수 Top 5" bordered={false}>
          {isLoadingDailyProductView && <ClipLoader />}
          {!isLoadingDailyProductView && <Line data={dailyProductViewData} />}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)' }}
          title="누적 구매자 지역 TOP 30"
          bordered={false}>
          {isLoadingBuyerRegionCounts && <ClipLoader />}
          {!isLoadingBuyerRegionCounts && <Bar data={buyerRegionCountsData} />}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>기간별 구매 출처</div>
              <div>
                <Button
                  onClick={async () => {
                    const fileName = `igogo-purchases-source-count-${moment(statisticsStore.startDate).format(
                      'YYYYMMDD',
                    )}-${moment(statisticsStore.endDate).format('YYYYMMDD')}.xlsx`;
                    const blob = await api.exportPurchaseSourceCounts(
                      statisticsStore.startDate,
                      statisticsStore.endDate,
                    );
                    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                      // IE variant
                      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                    } else {
                      const url = window.URL.createObjectURL(blob.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', fileName);
                      document.body.appendChild(link);
                      link.click();
                      link.parentNode && link.parentNode.removeChild(link);
                    }
                  }}>
                  엑셀 파일
                </Button>
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingPurchaseSourceCounts && <ClipLoader />}
          {!isLoadingPurchaseSourceCounts && (
            <Table
              scroll={{ x: 'max-content', y: '240px' }}
              pagination={false}
              bordered
              columns={[
                {
                  title: '구매 수',
                  align: 'center',
                  width: 100,
                  fixed: 'left',
                  render: (record) => `${record?.count}`,
                },
                {
                  title: '구매 출처 ID',
                  align: 'center',
                  fixed: 'left',
                  render: (record) => record?.eventId?.actionId ?? '-',
                },
                {
                  title: '구매 출처 이름',
                  align: 'center',
                  render: (record) => {
                    const actionId = record?.eventId?.actionId;
                    if (!actionId) {
                      return '-';
                    }

                    if (actionId.startsWith('purchased-products-cell')) {
                      const page = actionId.replace('purchased-products-cell-', '');
                      return `지금 막 결제되었어요(${page}번째 섹션)`;
                    }
                    const name = PURCHASE_SOURCE_NAME_MAP[actionId as keyof typeof PURCHASE_SOURCE_NAME_MAP];
                    if (!name) {
                      return actionId;
                    }
                    return name;
                  },
                },
                {
                  title: '비고',
                  align: 'center',
                  render: (record) => {
                    const collectionTitle = record?.collectionTitle;
                    const keyword = record?.eventId.keyword;
                    const partnerName = record?.partnerName;

                    return collectionTitle || keyword || partnerName || '-';
                  },
                },
              ]}
              dataSource={statisticsStore.purchaseSourceCounts || []}
            />
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>기간별 클릭 수 Top 10</div>
              <div>
                <Button
                  onClick={async () => {
                    const fileName = `igogo-clicks-${moment(statisticsStore.startDate).format('YYYYMMDD')}-${moment(
                      statisticsStore.endDate,
                    ).format('YYYYMMDD')}.xlsx`;
                    const blob = await api.exportClicksRank(statisticsStore.startDate, statisticsStore.endDate);
                    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                      // IE variant
                      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                    } else {
                      const url = window.URL.createObjectURL(blob.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', fileName);
                      document.body.appendChild(link);
                      link.click();
                      link.parentNode && link.parentNode.removeChild(link);
                    }
                  }}>
                  엑셀 파일
                </Button>
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingClicksRank && <ClipLoader />}
          {!isLoadingClicksRank && (
            <Table
              scroll={{ x: 'max-content', y: '240px' }}
              pagination={false}
              bordered
              columns={[
                {
                  title: '클릭 수',
                  align: 'center',
                  width: 100,
                  fixed: 'left',
                  render: (record) => `${record?.count}`,
                },
                {
                  title: '클릭 위치 ID',
                  align: 'center',
                  fixed: 'left',
                  render: (record) => `${record?._id?.actionId}`,
                },
                {
                  title: '클릭 동작',
                  align: 'center',
                  render: (record) => `${record?._id?.actionType}`,
                },
                {
                  title: '상품',
                  align: 'center',
                  render: (record) => `${record?.productTitle ? record?.productTitle.slice(0, 20) + '...' : ''}`,
                },
                {
                  title: '기획전',
                  align: 'center',
                  width: 240,
                  render: (record) => `${record?.collectionTitle ? record?.collectionTitle.slice(0, 20) + '...' : ''}`,
                },
                {
                  title: '검색어',
                  align: 'center',
                  width: 240,
                  render: (record) => `${record?._id?.options?.keyword || ''}`,
                },
                {
                  title: '링크',
                  align: 'center',
                  width: 240,
                  render: (record) => `${record?._id?.link || ''}`,
                },
              ]}
              dataSource={statisticsStore.clicksRank}
            />
          )}
        </Card>
        <Card className="chart" title="일별 구매 출처" bordered={false}>
          {isLoadingDailyPurchaseSourceCounts && <ClipLoader />}
          {!isLoadingDailyPurchaseSourceCounts && statisticsStore.dailyPurchaseSourceCounts && (
            <DailyPurchaseSourceChart data={statisticsStore.dailyPurchaseSourceCounts} />
          )}
        </Card>
        <Card className="chart" title="일별 결제 오류 수" bordered={false}>
          {isLoadingDailyPaymentError && <ClipLoader />}
          {!isLoadingDailyPaymentError && <Line data={dailyPaymentErrorCountData} />}
        </Card>
        <Card className="chart" title="일별 결제 타임아웃 수" bordered={false}>
          {isLoadingDailyPaymentReady && <ClipLoader />}
          {!isLoadingDailyPaymentReady && <Line data={dailyPaymentReadyCountData} />}
        </Card>
        <DiscoverFeedStatsCards data={discoverFeedStats} isLoading={isLoadingDiscoverFeedStats} />
        <ProductClickLocationChart
          data={statisticsStore.productClickLocationData || []}
          isLoading={isLoadingProductClickLocationData}
        />
        <ProductClickLocationRatioChart
          data={statisticsStore.productClickLocationData || []}
          isLoading={isLoadingProductClickLocationData}
        />
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)' }}
          title="파트너 프로필 조회 Top 30"
          bordered={false}>
          {isLoadingPartnerProfileViewCounts && <ClipLoader />}
          {!isLoadingPartnerProfileViewCounts && (
            <Table
              scroll={{ x: 'max-content', y: '320px' }}
              pagination={false}
              bordered
              rowKey={(record) => record.partnerId}
              columns={[
                {
                  title: '순위',
                  align: 'center',
                  width: 70,
                  render: (_value, _record, index) => index + 1,
                },
                {
                  title: '조회 수',
                  align: 'center',
                  width: 100,
                  render: (record) => `${record?.count?.toLocaleString?.() ?? record?.count}`,
                },
                {
                  title: '파트너',
                  align: 'center',
                  render: (record) => record?.partnerName || '-',
                },
                {
                  title: '파트너 ID',
                  align: 'center',
                  render: (record) => record?.partnerId || '-',
                },
              ]}
              dataSource={statisticsStore.partnerProfileViewCounts || []}
            />
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)' }}
          title="파트너 프로필 이동 클릭 Top 30"
          bordered={false}>
          {isLoadingPartnerProfileClickCounts && <ClipLoader />}
          {!isLoadingPartnerProfileClickCounts && (
            <Table
              scroll={{ x: 'max-content', y: '320px' }}
              pagination={false}
              bordered
              rowKey={(record) => `${record.partnerId}-${record.sourceId || 'unknown'}`}
              columns={[
                {
                  title: '순위',
                  align: 'center',
                  width: 70,
                  render: (_value, _record, index) => index + 1,
                },
                {
                  title: '클릭 수',
                  align: 'center',
                  width: 100,
                  render: (record) => `${record?.count?.toLocaleString?.() ?? record?.count}`,
                },
                {
                  title: '파트너',
                  align: 'center',
                  render: (record) => record?.partnerName || '-',
                },
                {
                  title: '클릭 위치',
                  align: 'center',
                  render: (record) => {
                    const sourceId = record?.sourceId;
                    if (!sourceId) {
                      return '-';
                    }
                    return PURCHASE_SOURCE_NAME_MAP[sourceId as keyof typeof PURCHASE_SOURCE_NAME_MAP] || sourceId;
                  },
                },
                {
                  title: '파트너 ID',
                  align: 'center',
                  render: (record) => record?.partnerId || '-',
                },
              ]}
              dataSource={statisticsStore.partnerProfileClickCounts || []}
            />
          )}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>일별 수업 인원</div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyClassCounts && <ClipLoader />}
          {!isLoadingDailyClassCounts && (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                data={dailyClassCountsData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top' as const,
                      labels: {
                        usePointStyle: true,
                        generateLabels: (chart: any) => {
                          const original = Chart.defaults.plugins.legend.labels.generateLabels;
                          const labels = original.call(this, chart);
                          labels.forEach((label: any) => {
                            if (label.text.includes('(예측)')) {
                              label.pointStyle = 'triangle';
                              label.lineDash = [5, 5];
                            }
                          });
                          return labels;
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y;
                          if (label.includes('(예측)')) {
                            return `${label}: ${value}명 (예측값)`;
                          }
                          return `${label}: ${value}명`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: '날짜',
                      },
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: '수업 인원',
                      },
                      beginAtZero: true,
                    },
                  },
                }}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>상품 카테고리별 주간 조회수 TOP 10</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatePicker
                  value={
                    statisticsStore.productViewsWeeklyStartDate
                      ? dayjs(statisticsStore.productViewsWeeklyStartDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setProductViewsWeeklyStartDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>-</span>
                <DatePicker
                  value={
                    statisticsStore.productViewsWeeklyEndDate ? dayjs(statisticsStore.productViewsWeeklyEndDate) : null
                  }
                  onChange={(date) => statisticsStore.setProductViewsWeeklyEndDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingProductViewsByCategoryWeekly && <ClipLoader />}
          {!isLoadingProductViewsByCategoryWeekly && (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                data={productViewsByCategoryWeeklyData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top' as const,
                      labels: {
                        usePointStyle: true,
                        generateLabels: (chart: any) => {
                          const original = Chart.defaults.plugins.legend.labels.generateLabels;
                          const labels = original.call(this, chart);
                          labels.forEach((label: any) => {
                            if (label.text.includes('(예측)')) {
                              label.pointStyle = 'triangle';
                              label.lineDash = [5, 5];
                            }
                          });
                          return labels;
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y;
                          if (label.includes('(예측)')) {
                            return `${label}: ${value} (예측값)`;
                          }
                          return `${label}: ${value}`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: '주차',
                      },
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: '조회수',
                      },
                      beginAtZero: true,
                    },
                  },
                }}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>상품 카테고리별 주간 조회 비율</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatePicker
                  value={
                    statisticsStore.productViewsWeeklyStartDate
                      ? dayjs(statisticsStore.productViewsWeeklyStartDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setProductViewsWeeklyStartDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>-</span>
                <DatePicker
                  value={
                    statisticsStore.productViewsWeeklyEndDate ? dayjs(statisticsStore.productViewsWeeklyEndDate) : null
                  }
                  onChange={(date) => statisticsStore.setProductViewsWeeklyEndDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingProductViewsByCategoryWeekly && <ClipLoader />}
          {!isLoadingProductViewsByCategoryWeekly && (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                data={productViewsByCategoryWeeklyRatiosData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top' as const,
                      labels: {
                        usePointStyle: true,
                        generateLabels: (chart: any) => {
                          const original = Chart.defaults.plugins.legend.labels.generateLabels;
                          const labels = original.call(this, chart);
                          labels.forEach((label: any) => {
                            if (label.text.includes('(예측)')) {
                              label.pointStyle = 'triangle';
                              label.lineDash = [5, 5];
                            }
                          });
                          return labels;
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y;
                          if (label.includes('(예측)')) {
                            return `${label}: ${value}% (예측값)`;
                          }
                          return `${label}: ${value}%`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: '주차',
                      },
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: '조회 비율 (%)',
                      },
                      beginAtZero: true,
                      max: 100,
                    },
                  },
                }}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                일별 다중지능 검사수
                {dailyAssessmentCounts?.data?.totalStats && (
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    (총 검사수: {dailyAssessmentCounts.data.totalStats.totalAssessmentCount}회, 검사 유저수:{' '}
                    {dailyAssessmentCounts.data.totalStats.totalUserCount}명)
                  </span>
                )}
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyAssessmentCounts ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <ClipLoader />
              <div style={{ marginTop: 16 }}>데이터 로딩 중...</div>
            </div>
          ) : (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      display: true,
                      position: 'left' as const,
                      title: {
                        display: true,
                        text: '일별 검사수 / 검사 유저수',
                      },
                      beginAtZero: true,
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y;
                          if (label.includes('검사수')) {
                            return `${label}: ${value}회`;
                          }
                          return `${label}: ${value}명`;
                        },
                      },
                    },
                  },
                }}
                data={dailyAssessmentCountsData}
                style={{ width: '100%', height: '100%' }}
                width={undefined}
              />
            </div>
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>일별 찜한 상품 수 및 누적 현황</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoriteProductCountsStartDate
                      ? dayjs(statisticsStore.dailyFavoriteProductCountsStartDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setDailyFavoriteProductCountsStartDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>-</span>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoriteProductCountsEndDate
                      ? dayjs(statisticsStore.dailyFavoriteProductCountsEndDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setDailyFavoriteProductCountsEndDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyFavoriteProductCounts ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <ClipLoader />
              <div style={{ marginTop: 16 }}>데이터 로딩 중...</div>
            </div>
          ) : (
            <div style={{ height: '100%', width: '100%' }}>
              <Line
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      display: true,
                      position: 'left' as const,
                      title: {
                        display: true,
                        text: '일별 찜한 상품 수',
                      },
                    },
                    y1: {
                      type: 'linear' as const,
                      display: true,
                      position: 'right' as const,
                      title: {
                        display: true,
                        text: '누적 찜한 상품 수',
                      },
                      grid: {
                        drawOnChartArea: false,
                      },
                    },
                  },
                }}
                data={{
                  labels: (statisticsStore.dailyFavoriteProductCounts || []).map((item) => {
                    const date = new Date(item.date);
                    const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
                    return `${item.date} (${dayOfWeek})`;
                  }),
                  datasets: [
                    {
                      label: '일별 찜한 상품 수',
                      data: (statisticsStore.dailyFavoriteProductCounts || []).map((item) =>
                        !item.isPrediction ? item.dailyCount : null,
                      ),
                      borderColor: '#1890ff',
                      backgroundColor: 'rgba(24, 144, 255, 0.1)',
                      fill: true,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      borderWidth: 2,
                    },
                    {
                      label: '일별 찜한 상품 수 (예측)',
                      data: (statisticsStore.dailyFavoriteProductCounts || []).map((item, index) => {
                        const data = statisticsStore.dailyFavoriteProductCounts || [];
                        const lastActualIndex = data.findIndex((item) => item.isPrediction) - 1;

                        if (index === lastActualIndex) {
                          return item.dailyCount;
                        } else if (item.isPrediction) {
                          return item.dailyCount;
                        }
                        return null;
                      }),
                      borderColor: '#1890ff',
                      backgroundColor: 'rgba(24, 144, 255, 0.05)',
                      borderDash: [5, 5],
                      fill: false,
                      tension: 0.1,
                      pointRadius: 3,
                      pointHoverRadius: 5,
                      pointStyle: 'triangle',
                      borderWidth: 2,
                    },
                    {
                      label: '누적 찜한 상품 수',
                      data: (statisticsStore.dailyFavoriteProductCounts || []).map((item) =>
                        !item.isPrediction ? item.cumulativeCount : null,
                      ),
                      borderColor: '#52c41a',
                      backgroundColor: 'rgba(82, 196, 26, 0.1)',
                      fill: false,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      yAxisID: 'y1',
                      borderWidth: 2,
                    },
                    {
                      label: '누적 찜한 상품 수 (예측)',
                      data: (statisticsStore.dailyFavoriteProductCounts || []).map((item, index) => {
                        const data = statisticsStore.dailyFavoriteProductCounts || [];
                        const lastActualIndex = data.findIndex((item) => item.isPrediction) - 1;

                        if (index === lastActualIndex) {
                          return item.cumulativeCount;
                        } else if (item.isPrediction) {
                          return item.cumulativeCount;
                        }
                        return null;
                      }),
                      borderColor: '#52c41a',
                      backgroundColor: 'rgba(82, 196, 26, 0.05)',
                      borderDash: [5, 5],
                      fill: false,
                      tension: 0.1,
                      pointRadius: 3,
                      pointHoverRadius: 5,
                      pointStyle: 'triangle',
                      yAxisID: 'y1',
                      borderWidth: 2,
                    },
                  ],
                }}
                style={{ width: '100%', height: '400px' }}
                width={undefined}
                height={400}
              />
            </div>
          )}
        </Card>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>일별 찜한 유저콘텐츠 수 및 누적 현황</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoriteUserContentsCountsStartDate
                      ? dayjs(statisticsStore.dailyFavoriteUserContentsCountsStartDate)
                      : null
                  }
                  onChange={(date) =>
                    statisticsStore.setDailyFavoriteUserContentsCountsStartDate(date?.toDate() || null)
                  }
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>-</span>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoriteUserContentsCountsEndDate
                      ? dayjs(statisticsStore.dailyFavoriteUserContentsCountsEndDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setDailyFavoriteUserContentsCountsEndDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyFavoriteUserContentsCounts ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <ClipLoader />
              <div style={{ marginTop: 16 }}>데이터 로딩 중...</div>
            </div>
          ) : (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      display: true,
                      position: 'left' as const,
                      title: {
                        display: true,
                        text: '일별 찜한 유저콘텐츠 수',
                      },
                    },
                    y1: {
                      type: 'linear' as const,
                      display: true,
                      position: 'right' as const,
                      title: {
                        display: true,
                        text: '누적 찜한 유저콘텐츠 수',
                      },
                      grid: {
                        drawOnChartArea: false,
                      },
                    },
                  },
                }}
                data={{
                  labels: (statisticsStore.dailyFavoriteUserContentsCounts || []).map((item) => {
                    const date = new Date(item.date);
                    const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
                    return `${item.date} (${dayOfWeek})`;
                  }),
                  datasets: [
                    {
                      label: '일별 찜한 유저콘텐츠 수',
                      data: (statisticsStore.dailyFavoriteUserContentsCounts || []).map((item) => item.dailyCount),
                      borderColor: '#1890ff',
                      backgroundColor: 'rgba(24, 144, 255, 0.1)',
                      fill: true,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      borderWidth: 2,
                    },
                    {
                      label: '누적 찜한 유저콘텐츠 수',
                      data: (statisticsStore.dailyFavoriteUserContentsCounts || []).map((item) => item.cumulativeCount),
                      borderColor: '#52c41a',
                      backgroundColor: 'rgba(82, 196, 26, 0.1)',
                      fill: false,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      yAxisID: 'y1',
                      borderWidth: 2,
                    },
                  ],
                }}
                style={{ width: '100%', height: '100%' }}
                width={undefined}
              />
            </div>
          )}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>일별 찜한 파트너 수 및 누적 현황</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoritePartnerCountsStartDate
                      ? dayjs(statisticsStore.dailyFavoritePartnerCountsStartDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setDailyFavoritePartnerCountsStartDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>-</span>
                <DatePicker
                  value={
                    statisticsStore.dailyFavoritePartnerCountsEndDate
                      ? dayjs(statisticsStore.dailyFavoritePartnerCountsEndDate)
                      : null
                  }
                  onChange={(date) => statisticsStore.setDailyFavoritePartnerCountsEndDate(date?.toDate() || null)}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyFavoritePartnerCounts ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <ClipLoader />
              <div style={{ marginTop: 16 }}>데이터 로딩 중...</div>
            </div>
          ) : (
            <div style={{ height: '400px', width: '100%' }}>
              <Line
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      display: true,
                      position: 'left' as const,
                      title: {
                        display: true,
                        text: '일별 찜한 파트너 수',
                      },
                    },
                    y1: {
                      type: 'linear' as const,
                      display: true,
                      position: 'right' as const,
                      title: {
                        display: true,
                        text: '누적 찜한 파트너 수',
                      },
                      grid: {
                        drawOnChartArea: false,
                      },
                    },
                  },
                }}
                data={{
                  labels: (statisticsStore.dailyFavoritePartnerCounts || []).map((item) => {
                    const date = new Date(item.date);
                    const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
                    return `${item.date} (${dayOfWeek})`;
                  }),
                  datasets: [
                    {
                      label: '일별 찜한 파트너 수',
                      data: (statisticsStore.dailyFavoritePartnerCounts || []).map((item) => item.dailyCount),
                      borderColor: '#1890ff',
                      backgroundColor: 'rgba(24, 144, 255, 0.1)',
                      fill: true,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      borderWidth: 2,
                    },
                    {
                      label: '누적 찜한 파트너 수',
                      data: (statisticsStore.dailyFavoritePartnerCounts || []).map((item) => item.cumulativeCount),
                      borderColor: '#52c41a',
                      backgroundColor: 'rgba(82, 196, 26, 0.1)',
                      fill: false,
                      tension: 0.1,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      yAxisID: 'y1',
                      borderWidth: 2,
                    },
                  ],
                }}
                style={{ width: '100%', height: '100%' }}
                width={undefined}
              />
            </div>
          )}
        </Card>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Card
          className="donut-chart"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>검색어 TOP 10</div>
              <Button
                onClick={async () => {
                  const fileName = `igogo-search-keywords-${moment(statisticsStore.startDate).format(
                    'YYYYMMDD',
                  )}-${moment(statisticsStore.endDate).format('YYYYMMDD')}.xlsx`;
                  const blob = await api.exportSearchTextRank(statisticsStore.startDate, statisticsStore.endDate);
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일
              </Button>
            </div>
          }
          bordered={false}>
          {isLoadingSearchText && <ClipLoader />}
          {!isLoadingSearchText && <Doughnut data={searchTextData} />}
        </Card>
        <Card
          className="donut-chart"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>결과0 검색어 TOP 10</div>
              <Button
                onClick={async () => {
                  const fileName = `igogo-no-result-search-keywords-${moment(statisticsStore.startDate).format(
                    'YYYYMMDD',
                  )}-${moment(statisticsStore.endDate).format('YYYYMMDD')}.xlsx`;
                  const blob = await api.exportNoResultSearchTextRank(
                    statisticsStore.startDate,
                    statisticsStore.endDate,
                  );
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일
              </Button>
            </div>
          }
          bordered={false}>
          {isLoadingNoResultSearchQuery && <ClipLoader />}
          {!isLoadingNoResultSearchQuery && <Doughnut data={noResultSearchTextData} />}
        </Card>
        <Card
          className="donut-chart"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                날짜 검색 TOP 10
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                  }}>{` (${statisticsStore.dateSearchRank?.dateSearchCount}/${statisticsStore.dateSearchRank?.totalCount})`}</span>
              </div>

              <Button
                onClick={async () => {
                  const fileName = `igogo-date-search-${moment(statisticsStore.startDate).format('YYYYMMDD')}-${moment(
                    statisticsStore.endDate,
                  ).format('YYYYMMDD')}.xlsx`;
                  const blob = await api.exportDateSearchRank(statisticsStore.startDate, statisticsStore.endDate);
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일
              </Button>
            </div>
          }
          bordered={false}>
          {isLoadingDateSearchRank && <ClipLoader />}
          {!isLoadingDateSearchRank && <Doughnut data={dateSearchRankData} />}
        </Card>
      </div>
    </div>
  );
};

export default UserBehaviorTab;
